import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../Store/hooks';
import { forgotPassword, resetForgotPassword } from '../Store/StudentSlices/forgotPasswordSlice';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [email, setEmail] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const fromSettings = (location.state as { from?: string } | null)?.from === 'settings';

  const { loading, error, errorMessage, success } = useAppSelector(
    (state) => state.forgotPassword
  );

  useEffect(() => {
    if (success) {
      setIsSuccess(true);
    }
  }, [success]);

  useEffect(() => {
    return () => {
      dispatch(resetForgotPassword());
    };
  }, [dispatch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      return;
    }
    const result = await dispatch(forgotPassword({ email }));
    if (forgotPassword.fulfilled.match(result)) {
      setIsSuccess(true);
    }
  };

  return (
    <div className="min-h-screen bg-white px-4 py-10 flex flex-col">
      <div className="w-full max-w-md mx-auto flex-1 flex flex-col justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate(fromSettings ? '/' : '/login')}
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            {fromSettings ? 'Back to Home' : 'Back to Login'}
          </button>
          <div className="text-center mt-6">
            <h1 className="text-lg font-semibold text-gray-900">Onefinnet Coaching</h1>
            <p className="text-xs text-gray-500">Expert coaching, personalized growth</p>
          </div>
        </div>

        <div className="mt-12">
          {isSuccess ? (
            <div className="text-center">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-blue-600 text-white">
                <svg
                  className="h-7 w-7"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-base font-semibold text-gray-900">Check Your Email</h2>
              <p className="mt-2 text-xs text-gray-500">
                We've sent a password reset link to <span className="font-semibold">{email}</span>.
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Please check your inbox and click the link to reset your password.
              </p>
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => navigate(fromSettings ? '/' : '/login', { replace: true })}
                  className="px-8 py-2.5 rounded-lg bg-gray-900 text-white text-xs font-semibold hover:bg-gray-800"
                >
                  {fromSettings ? 'Back to Home' : 'Back to Login'}
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div className="text-center">
                <h2 className="text-base font-semibold text-gray-900">Forgot Password?</h2>
                <p className="mt-2 text-xs text-gray-500">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                {error && errorMessage && (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
                    {errorMessage}
                  </div>
                )}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:border-gray-400"
                    placeholder="Enter your email"
                    autoComplete="email"
                    required
                  />
                </div>

                <div className="flex items-center justify-center">
                  <button
                    type="submit"
                    disabled={!email.trim() || loading}
                    className="px-10 py-2.5 rounded-lg bg-gray-900 text-white text-xs font-semibold hover:bg-gray-800 disabled:opacity-70"
                  >
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        <div className="mb-4 text-center text-xs text-gray-500">
          If you need help,{' '}
          <button type="button" className="underline text-gray-800">
            Contact Support
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
